import React, { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import SectionLabel from '../../SectionLabel';
import useAPI from '../../../api';
import useTranslate from '../../../Hooks/useTranslate';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import useLayoutEdit, { alongPath, freeName } from './useLayoutEdit';
import WidgetPalette from './WidgetPalette';
import RegionConfig from './RegionConfig';
import KeyPicker from './KeyPicker';
import EntryDialog from './EntryDialog';
import PresetDialog from './PresetDialog';
import { FieldEntryRow } from './LayoutRows';
import SortableRows, { reordered } from './SortableRows';
import { cellsForType } from '../columns/CellRegistry';
import useWidgetPresets from '../detail/useWidgetPresets';
import {
  WIDGET_TYPES,
  childWidgets,
  widgetEntries,
  widgetIsRequired,
  widgetPreset,
  widgetUsesTitle,
} from '../detail/WidgetRegistry';
import { ENTRY_KINDS, entryUsesKey } from '../detail/EntryRegistry';

/** How much of a row each kind takes when it is added. */
const NEW_SPAN = { field: 6, spacer: 6, heading: 12, divider: 12 };

/**
 * A key added to a region, drawn by the cell its type is usually drawn by and taking half the row.
 *
 * Half rather than all of it because a widget's keys read as a grid: a field that takes the whole
 * row unless told otherwise makes every new one a line of its own, and the arrangement has to be
 * undone before it can begin.
 */
const newEntry = ({ key, label, type }, defaults = null) => ({
  key,
  label,
  cell: cellsForType(type)[0]?.cell,
  size: { xs: 12, sm: NEW_SPAN.field },
  ...(defaults ?? {}),
});

/**
 * An entry that reads nothing - a rule, a heading, a gap - under a name no other entry holds.
 *
 * It carries a key like any other, since that is what the region lists it by and what the widget
 * draws it under, even though there is nothing behind it to read.
 */
const newKindEntry = (kind, entries) => ({
  key: freeName(
    (entries ?? []).map(({ key }) => key),
    kind
  ),
  kind,
  size: { xs: 12, sm: NEW_SPAN[kind] ?? 12 },
});

/**
 * The sidebar while a layout is being edited.
 *
 * One thing at a time, each with the whole column: the widgets to drag onto the page, or the region
 * chosen on it, or the keys that region could still show, or the field of it that is open. Every
 * step but the first has a way back to the one before.
 *
 * Two lists in one column is what this replaced, and neither of them had the room: the fields a
 * region holds and the keys it could hold are different moments of the same job, and showing both
 * at once halves each of them.
 *
 * It takes the place of the routes for as long as the mode lasts. Navigating away from the page
 * being arranged would end the mode anyway, so the routes have nothing to offer meanwhile, and the
 * canvas keeps the whole of the page.
 *
 * @returns {React.JSX.Element}
 */
/**
 * The entry a place refers to: one of the region's, or one held by a group of it.
 *
 * @param {Array} entries - The region's entries.
 * @param {Number|Array} at - The place, as an index or a `[group, child]` pair.
 *
 * @returns {Object|null}
 */
const entryAt = (entries, at) => {
  if (at === null || at === undefined) return null;

  return (
    (Array.isArray(at) ? entries?.[at[0]]?.entries?.[at[1]] : entries?.[at]) ??
    null
  );
};

/** Where in its own list a place sits. */
const placeIn = (at) => (Array.isArray(at) ? at[1] : at);

/**
 * The entries beside the one at a place: the rest of whichever list it sits in.
 *
 * @param {Array} entries - The region's entries.
 * @param {Number|Array} at - The place, as an index or a `[group, child]` pair.
 *
 * @returns {Array}
 */
const siblingsOf = (entries, at) => {
  const list = Array.isArray(at) ? (entries[at[0]]?.entries ?? []) : entries;

  return list.filter((_, index) => index !== placeIn(at));
};

const LayoutEditSidebar = function LayoutEditSidebar() {
  const intl = useIntl();
  const translate = useTranslate();
  const API = useAPI();
  const {
    session,
    regions,
    openPath,
    trail,
    container,
    selected,
    select,
    selectEntry,
    open,
    openTo,
    addChild,
    setRegions,
    updateRegion,
    updateEntries,
    removeRegion,
  } = useLayoutEdit();
  const {
    presets,
    save: savePreset,
    remove: removePreset,
  } = useWidgetPresets();
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const [catalogue, setCatalogue] = useState([]);
  // What adding an entry has got as far as: nothing, choosing what to add, or choosing which key.
  const [adding, setAdding] = useState(null);
  // Which list the entry being added goes into: the region's, or a group of it.
  const [addingInto, setAddingInto] = useState(null);
  const [configuring, setConfiguring] = useState(false);
  // The region being saved as a preset, or null when none is.
  const [naming, setNaming] = useState(null);

  const sectionName = session?.sectionName;

  useEffect(() => {
    if (!sectionName) return;

    API.getColumnCatalogue(
      sectionName,
      { silent: true },
      (res) => setCatalogue(res ?? []),
      () => setCatalogue([])
    );
  }, [sectionName]);

  const entries = selected?.entries ?? [];
  // Which entry is open: its place in the region's list, or its place inside a group of it.
  const openEntry = session?.selectedEntry ?? null;
  const entry = entryAt(entries, openEntry);

  // A widget that already shows something without being told to shows it here too: an empty list
  // beside a full card says the card holds nothing, which is not what it says on the page. Written
  // into the layout on the first look, so what is listed is what will be saved.
  useEffect(() => {
    if (!selected || entries.length > 0) return;

    const known = widgetEntries(selected.widget);

    if (known.length > 0) updateRegion({ entries: known });
  }, [selected, entries.length, updateRegion]);

  // The collection the region reads, whether it names one itself or takes one from the preset it is.
  const source = selected?.source ?? widgetPreset(selected?.widget)?.source;

  // A region reading a collection addresses its keys inside one item of it, so what it offers and
  // what it reads types from is that collection's own catalogue: `products[].amount`, as `amount`.
  const scoped = React.useMemo(() => {
    if (!source) return catalogue ?? [];

    const prefix = `${source}[].`;

    return (catalogue ?? [])
      .filter(({ key }) => key.startsWith(prefix))
      .map((known) => ({ ...known, key: known.key.slice(prefix.length) }));
  }, [catalogue, source]);

  // Types by key, so the open field is handed its own without searching the catalogue every render.
  const types = React.useMemo(
    () => Object.fromEntries(scoped.map((known) => [known.key, known.type])),
    [scoped]
  );

  /** What a region is called: its own label, its widget's, or the fallback given. */
  const nameOf = (region, fallback) =>
    translate(
      region?.label ??
        WIDGET_TYPES.find(({ widget }) => widget === region?.widget)?.label ??
        fallback
    );

  /** What the region is called, which is where a field of it or its keys go back to. */
  const regionName = nameOf(selected, selected?.widget);

  // A container is opened rather than selected, so its children are what the sidebar is about while
  // the canvas goes on showing the page.
  const inContainer = openPath.length > 0 && !selected;

  /** What a container on the path is called, for the way back out of it. */
  const containerName = (region) => nameOf(region, region?.id);

  const onRemovePreset = (id) =>
    removePreset(id, () =>
      openSnackbar({
        message: intl.messages.PRESET_DELETED,
        severity: 'success',
      })
    );

  /** What the level being configured is called: the container holding it, or the page. */
  const levelName = container
    ? containerName(container)
    : intl.messages.WIDGETS;

  /**
   * The step back out of whichever pane is showing, and what it goes back to.
   *
   * Leaving a container goes out one level rather than back to the page, so the way in and the way
   * out are the same however deep it went.
   */
  const back =
    adding === 'keys'
      ? { text: intl.messages.ADD, onClick: () => setAdding('kind') }
      : adding
        ? {
            text: inContainer ? levelName : regionName,
            onClick: () => setAdding(null),
          }
        : entry
          ? { text: regionName, onClick: () => selectEntry(null) }
          : selected
            ? { text: levelName, onClick: () => select('') }
            : inContainer
              ? {
                  text:
                    trail.length > 1
                      ? containerName(trail[trail.length - 2])
                      : intl.messages.WIDGETS,
                  onClick: () => openTo(openPath.slice(0, -1)),
                }
              : null;

  return (
    <>
      <ColumnFlexBox gap="12px" sx={{ flex: 1, minHeight: 0, p: '16px' }}>
        {back && (
          <>
            <Button
              id="layout-edit-back-btn"
              size="small"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={back.onClick}
              sx={{ flex: 'none', justifyContent: 'flex-start' }}
            >
              {back.text}
            </Button>
            <Divider sx={{ flex: 'none' }} />
          </>
        )}

        {/* The way in, when it went further than one container: a step back names the level above,
            which at depth says nothing about where that level sits. */}
        {openPath.length > 1 && (
          <CenteredFlexBox gap="2px" sx={{ flex: 'none', flexWrap: 'wrap' }}>
            <Button
              id="trail-page-btn"
              size="small"
              color="inherit"
              onClick={() => openTo([])}
              sx={{ minWidth: 0, textTransform: 'none' }}
            >
              {intl.messages.WIDGETS}
            </Button>
            {trail.map((region, at) => (
              <React.Fragment key={region.id}>
                <Typography variant="caption" sx={{ opacity: '50%' }}>
                  ›
                </Typography>
                <Button
                  id={`trail-${region.id}-btn`}
                  size="small"
                  color={at === trail.length - 1 ? 'primary' : 'inherit'}
                  onClick={() => openTo(openPath.slice(0, at + 1))}
                  sx={{ minWidth: 0, textTransform: 'none' }}
                >
                  {containerName(region)}
                </Button>
              </React.Fragment>
            ))}
          </CenteredFlexBox>
        )}

        {!selected && !inContainer && (
          <>
            <SectionLabel>{intl.messages.WIDGETS}</SectionLabel>
            <WidgetPalette onRemovePreset={onRemovePreset} />
          </>
        )}

        {/* What a container holds, in the order it holds it. There is nowhere to drag these to:
            a tab's contents fill the tab, so what there is to say is which widget and what it is
            called - and the page behind stays on screen while it is said. */}
        {inContainer && !adding && (
          <>
            <SectionLabel>{intl.messages.REGIONS}</SectionLabel>
            {regions.length === 0 && (
              <Typography variant="body2" sx={{ opacity: '70%' }}>
                {intl.messages.PICK_KEYS_FOR_REGION}
              </Typography>
            )}
            {/* The order they are listed in is the order they are drawn in - which for a tabbed
                container is the order of its tabs - so it is dragged rather than declared. */}
            <SortableRows
              items={regions}
              keyOf={({ id }) => id}
              onReorder={(from, to) => setRegions(reordered(regions, from, to))}
            >
              {(child) => (
                <ListItemButton
                  sx={{ borderRadius: '8px' }}
                  onClick={() => select(child.id)}
                >
                  <DragIndicatorRoundedIcon
                    fontSize="small"
                    sx={{ opacity: 0.5, mr: '8px' }}
                  />
                  <ListItemText
                    primary={containerName(child)}
                    secondary={child.id}
                  />
                  {!widgetIsRequired(child.widget) && (
                    <IconButton
                      id={`remove-child-${child.id}`}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRegion(child.id);
                      }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              )}
            </SortableRows>
            <Button
              id="add-child-btn"
              startIcon={<AddRoundedIcon />}
              onClick={() => setAdding('widget')}
              sx={{ flex: 'none' }}
            >
              {intl.messages.ADD}
            </Button>
          </>
        )}

        {/* The same options the page is given, out of what this container allows: a preset is a
            widget as far as a layout is concerned, so it belongs wherever one does. Picked rather
            than dragged, since a child fills its container and has nowhere to be dropped. */}
        {inContainer && adding === 'widget' && (
          <>
            <SectionLabel>{intl.messages.WIDGETS}</SectionLabel>
            <WidgetPalette
              widgets={childWidgets(container?.widget)}
              onPick={(widget) => {
                addChild(widget);
                setAdding(null);
              }}
              onRemovePreset={onRemovePreset}
            />
          </>
        )}

        {/* What is being added is chosen before what it holds: most entries are a key of the
            section, but a rule, a heading or a gap read nothing and have no key to pick. */}
        {selected && adding === 'kind' && (
          <>
            <SectionLabel>{intl.messages.ADD}</SectionLabel>
            <List dense disablePadding>
              {ENTRY_KINDS.map(({ kind, label }) => (
                <ListItemButton
                  key={kind}
                  sx={{ borderRadius: '8px' }}
                  onClick={() => {
                    if (entryUsesKey(kind)) {
                      setAdding('keys');
                      return;
                    }

                    updateEntries((held) =>
                      alongPath(held, addingInto, (list) => [
                        ...list,
                        newKindEntry(kind, list),
                      ])
                    );
                    setAdding(null);
                    setAddingInto(null);
                  }}
                >
                  <ListItemText primary={translate(label)} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {selected && adding === 'keys' && (
          <>
            <SectionLabel>{intl.messages.AVAILABLE}</SectionLabel>
            <KeyPicker
              catalogue={scoped}
              entries={entries}
              surface="detail"
              onPick={(picked) => {
                updateEntries((held) =>
                  alongPath(held, addingInto, (list) => [
                    ...list,
                    newEntry(picked, selected?.entryDefaults),
                  ])
                );
                setAdding(null);
                setAddingInto(null);
              }}
            />
          </>
        )}

        {selected && !adding && entry && (
          <ColumnFlexBox
            gap="8px"
            // A field's floating label is drawn above its border, which the scroll box would
            // otherwise cut off the first row.
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: '8px' }}
          >
            <FieldEntryRow
              entry={entry}
              index={placeIn(openEntry)}
              type={types[entry.key]}
              titled={widgetUsesTitle(selected.widget)}
              parameters={session?.parameters ?? []}
              // Only one entry can be the heading, so making one of them it takes it from the rest.
              onChange={(index, changes) =>
                updateEntries((held) =>
                  alongPath(held, openEntry, (list) =>
                    list.map((current, at) => {
                      if (at === index) return { ...current, ...changes };

                      return changes.isTitle && current.isTitle
                        ? { ...current, isTitle: undefined }
                        : current;
                    })
                  )
                )
              }
              onRemove={() => {
                updateEntries((held) =>
                  alongPath(held, openEntry, (list) =>
                    list.filter((_, at) => at !== placeIn(openEntry))
                  )
                );
                selectEntry(null);
              }}
              onConfigure={() => setConfiguring(true)}
            />
          </ColumnFlexBox>
        )}

        {selected && !adding && !entry && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <RegionConfig
              region={selected}
              onChange={updateRegion}
              onEntries={updateEntries}
              onOpenEntry={selectEntry}
              onAddEntry={(intoGroup) => {
                // A group is added into by naming it: the entry then joins its list rather than
                // the region's. Tested for what it has to be rather than against null - anything
                // else names a list nothing sits in, and the add then does nothing without
                // saying so.
                setAddingInto(Number.isInteger(intoGroup) ? [intoGroup] : null);
                setAdding('kind');
              }}
              onOpenRegions={open}
              onSavePreset={setNaming}
            />
          </Box>
        )}
      </ColumnFlexBox>
      {/* A detail entry is drawn by the same cells a list column is, and configured through the same
          dialog - as a detail entry, since what a region gives it is room rather than a place on a
          card. */}
      <EntryDialog
        open={configuring}
        close={() => setConfiguring(false)}
        entry={entry}
        surface="detail"
        // The keys a second one may name are the ones beside it, which for a region reading a
        // collection are that item's rather than the customer's.
        catalogue={scoped}
        parameters={session?.parameters ?? []}
        siblings={siblingsOf(entries, openEntry)}
        onConfirm={(changes) =>
          updateEntries((held) =>
            alongPath(held, openEntry, (list) =>
              list.map((current, at) =>
                at === placeIn(openEntry) ? { ...current, ...changes } : current
              )
            )
          )
        }
      />
      <PresetDialog
        open={Boolean(naming)}
        close={() => setNaming(null)}
        region={naming}
        presets={presets}
        onSave={(row) =>
          savePreset(row, () =>
            openSnackbar({
              message: intl.messages.PRESET_SAVED,
              severity: 'success',
            })
          )
        }
      />
    </>
  );
};

export default LayoutEditSidebar;
